// Formatting and safety helpers of the "Zásilky" module (dates, relative time, URLs, plurals).
// Everything that comes from e-mails is untrusted: URLs are only used when they are plain https, ids only when numeric.

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

// "2026-10-28" -> Date at local midnight (no timezone shift); a full ISO string -> its instant; otherwise null
export function toDate(value) {
  if (!value) return null;
  const only = DATE_ONLY.exec(String(value));
  const d = only ? new Date(Number(only[1]), Number(only[2]) - 1, Number(only[3])) : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

// "28. 10. 2026"
export function formatDate(value) {
  const d = toDate(value);
  return d ? `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}` : '';
}

// "28. 10. 2026 14:32"
export function formatDateTime(value) {
  const d = toDate(value);
  if (!d) return '';
  const time = d.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' });
  return `${formatDate(value)} ${time}`;
}

// "1 den" / "2 dny" / "5 dnů"
export function plural(n, one, few, many) {
  const abs = Math.abs(n);
  if (abs === 1) return `${n} ${one}`;
  if (abs >= 2 && abs <= 4) return `${n} ${few}`;
  return `${n} ${many}`;
}

// "právě teď", "před 5 min", "před 3 h", "včera", "před 4 dny", else the date
export function relativeTime(value, now = Date.now()) {
  const d = toDate(value);
  if (!d) return '';
  const diff = Math.round((now - d.getTime()) / 1000);
  if (diff < 0) return formatDate(value);
  if (diff < 60) return 'právě teď';
  if (diff < 3600) return `před ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `před ${Math.floor(diff / 3600)} h`;
  const days = Math.floor(diff / 86400);
  if (days === 1) return 'včera';
  if (days < 14) return `před ${plural(days, 'dnem', 'dny', 'dny')}`;
  return formatDate(value);
}

// Days from today to a calendar date (negative = in the past); null when the date is unknown
export function daysFromToday(value) {
  const d = toDate(value);
  if (!d) return null;
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((new Date(d.getFullYear(), d.getMonth(), d.getDate()) - start) / 86400000);
}

// Calendar date input value ("YYYY-MM-DD") from a date or an ISO timestamp
export function toDateInput(value) {
  const d = toDate(value);
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Links from the server are only ever opened when they pass these checks; everything else is dropped (no link rendered).
// Common rules: a string, plain https, a host, NO credentials (user:pass@ / user@), no explicit port, no whitespace,
// control characters or backslashes. Accepted / rejected examples (re-checked by a node one-liner when this changes):
//   safeHttpsUrl (carrier tracking links)
//     accept  https://www.ppl.cz/vyhledat-zasilku?shipmentId=123      https://tracking.packeta.com/cs/?id=Z123
//     reject  http://www.ppl.cz/x (not https)          javascript:alert(1)   data:text/html,x   //evil.example/x   /relative
//             https://user:pass@www.ppl.cz/x (credentials)    https://user@www.ppl.cz/x (userinfo)
//             https://www.ppl.cz:8443/x (port)         https://www.ppl.cz\@evil.example/ (backslash)    '', null, 42
//   safeGmailUrl (gmailUrl): the same plus the host must be EXACTLY mail.google.com
//     accept  https://mail.google.com/mail/?authuser=info%40basicgame.net#all/FMfcgz123
//     reject  https://mail.google.com.evil.example/mail/   https://evilmail.google.com/   https://google.com/
//             https://notmail.google.com/   https://mail.google.com:8443/   https://user:pass@mail.google.com/   http://mail.google.com/
// space, control characters (<= 0x20, 0x7f) and the backslash are never part of an accepted URL
function hasUnsafeChars(text) {
  for (let i = 0; i < text.length; i += 1) {
    const c = text.charCodeAt(i);
    if (c <= 0x20 || c === 0x7f || c === 0x5c) return true;
  }
  return false;
}

function parseSafeUrl(value) {
  if (typeof value !== 'string' || value.length > 2048 || !value.startsWith('https://')) return null;
  if (hasUnsafeChars(value)) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' || !url.hostname) return null;
    if (url.username || url.password || url.port) return null;
    return url;
  } catch {
    return null;
  }
}

export function safeHttpsUrl(value) {
  return parseSafeUrl(value)?.href ?? null;
}

export function safeGmailUrl(value) {
  const url = parseSafeUrl(value);
  return url && url.hostname === 'mail.google.com' ? url.href : null;
}

// Entity link built from a numeric id only, never from text
export function entityPath(kind, id) {
  const n = Number(id);
  if (!Number.isInteger(n) || n <= 0) return null;
  const base = { customer: '/customers', machine: '/machines', supplier: null, order: null }[kind];
  return base ? `${base}/${n}` : null;
}

export function formatPercent(value) {
  const n = Number(value);
  return Number.isFinite(n) ? `${Math.round(n * 100)} %` : '';
}

export function formatMoney(amount, currency) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '';
  const digits = Number.isInteger(n) ? 0 : 2;
  return `${n.toLocaleString('cs-CZ', { minimumFractionDigits: digits, maximumFractionDigits: 2 })} ${currency || ''}`.trim();
}

export function formatUsd(value) {
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD` : '–';
}

// manualFields may come as camelCase or snake_case; compare in camelCase
export function camelize(key) {
  return String(key).replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

// Copies text to the clipboard; resolves true when it worked
export async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.cssText = 'position:fixed;opacity:0;top:0;left:0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

// Plain text of an extraction value (string, number or { amount, currency }) for the review cards
export function plainValue(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'object') {
    if ('amount' in value) return formatMoney(value.amount, value.currency);
    return '';
  }
  return String(value);
}

// Row highlight for the list: problem > delayed > stuck (the status badge and tags carry the same information as text)
export function rowTone(row) {
  if (row.status === 'problem') return 'problem';
  if (row.flags?.delayed) return 'delayed';
  if (row.flags?.stuck) return 'stuck';
  return '';
}

// "28. 10. – 30. 10."
export function formatRange(from, to) {
  if (from && to) return `${formatDate(from)} – ${formatDate(to)}`;
  return formatDate(from || to);
}
